<script>
  import Icon from "@iconify/svelte";
  import { onMount, onDestroy } from "svelte";
  import { t } from "../lib/i18n.svelte.js";
  import { api } from "../lib/api.svelte.js";
  import { push } from "svelte-spa-router";
  import Layout from "../components/Layout.svelte";
  import Skeleton from "../components/Skeleton.svelte";
  import { Chart, registerables } from "chart.js";

  Chart.register(...registerables);

  let loading = $state(true);
  let error = $state("");
  let stats = $state(null);

  // Chart refs
  let activityChartEl = $state(null);
  let sessionsBarEl = $state(null);
  let invitePieEl = $state(null);
  let viewerPieEl = $state(null);
  let sitePieEl = $state(null);
  let sparkEls = $state({});
  let charts = [];

  function destroyAll() {
    charts.forEach((c) => c?.destroy());
    charts = [];
  }

  const metricDefs = [
    { key: "sites_count", icon: "mdi:web", labelKey: "dashboard.sites", href: "/sites", color: "#3b82f6", sparkKey: "sites_per_day" },
    { key: "viewers_count", icon: "mdi:account-group", labelKey: "dashboard.viewers", href: "/viewers", color: "#8b5cf6", sparkKey: "viewers_per_day" },
    { key: "active_sessions", icon: "mdi:lan-connect", labelKey: "dashboard.sessions", href: "/access", color: "#22c55e", sparkKey: "sessions_per_day" },
    { key: "invites_total", icon: "mdi:email-outline", labelKey: "dashboard.invites", href: "/invites", color: "#f59e0b", sparkKey: "invites_per_day" },
  ];

  let metrics = $derived(
    metricDefs.map((d) => ({
      ...d,
      label: d.label || t(d.labelKey),
      value: stats?.[d.key] ?? 0,
      sparkData: (stats?.[d.sparkKey] || []).map((p) => p.count),
    }))
  );

  // Derived sub-stats for inline display under metric cards
  let siteSub = $derived(stats ? `${stats.sites_public || 0} ${t("dashboard.public")}, ${stats.sites_private || 0} ${t("dashboard.private")}` : "");
  let viewerSub = $derived(stats ? `${stats.viewers_active || 0} ${t("dashboard.active")}, ${stats.viewers_revoked || 0} ${t("dashboard.revoked")}` : "");
  let sessionSub = $derived(stats ? `${stats.active_sessions || 0} ${t("dashboard.live")}` : "");
  let inviteSub = $derived(stats ? `${stats.invites_claimed || 0} ${t("dashboard.claimed")}, ${stats.invites_pending || 0} ${t("dashboard.pending")}` : "");
  let metricSubs = $derived([siteSub, viewerSub, sessionSub, inviteSub]);

  async function loadStats() {
    try {
      stats = await api.get("/dashboard/stats?days=30");
    } catch {
      try {
        const basic = await api.get("/dashboard");
        stats = {
          sites_count: basic.sites || 0, viewers_count: basic.viewers || 0,
          active_sessions: basic.active_sessions || 0, invites_total: 0,
          invites_claimed: 0, invites_pending: 0, invites_revoked: 0, invites_expired: 0,
          viewers_active: 0, viewers_revoked: 0, sites_public: 0, sites_private: 0,
          invites_per_day: [], sessions_per_day: [], viewers_per_day: [], sites_per_day: [],
          recent_invites: [], recent_sessions: [],
        };
      } catch (e2) {
        error = e2.message || t("common.error");
      }
    }
    loading = false;
  }

  function hexToRgba(hex, alpha) {
    const r = parseInt(hex.slice(1, 3), 16);
    const g = parseInt(hex.slice(3, 5), 16);
    const b = parseInt(hex.slice(5, 7), 16);
    return `rgba(${r},${g},${b},${alpha})`;
  }

  // Sparkline: tiny inline chart for metric cards
  function buildSparkline(canvas, data, color) {
    if (!canvas || !data?.length) return null;
    const c = new Chart(canvas, {
      type: "line",
      data: {
        labels: data.map((_, i) => i),
        datasets: [{
          data,
          borderColor: color,
          backgroundColor: hexToRgba(color, 0.15),
          fill: true,
          tension: 0.4,
          pointRadius: 0,
          borderWidth: 1.5,
        }],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: { legend: { display: false }, tooltip: { enabled: false } },
        scales: {
          x: { display: false },
          y: { display: false, beginAtZero: true },
        },
        elements: { line: { capBezierPoints: false } },
      },
    });
    charts.push(c);
    return c;
  }

  function buildActivityChart() {
    if (!activityChartEl || !stats) return;
    const labels = (stats.sessions_per_day || []).map((d) =>
      new Date(d.date).toLocaleDateString(undefined, { month: "short", day: "numeric" })
    );
    const c = new Chart(activityChartEl, {
      type: "line",
      data: {
        labels,
        datasets: [
          makeLineDataset(t("dashboard.sessions"), stats.sessions_per_day, "#22c55e"),
          makeLineDataset(t("dashboard.invites"), stats.invites_per_day, "#f59e0b"),
          makeLineDataset(t("dashboard.viewers"), stats.viewers_per_day, "#8b5cf6"),
          makeLineDataset(t("dashboard.sites"), stats.sites_per_day, "#3b82f6"),
        ],
      },
      options: lineOpts(),
    });
    charts.push(c);
  }

  function buildSessionsBar() {
    if (!sessionsBarEl || !stats?.sessions_per_day?.length) return;
    const days = stats.sessions_per_day;
    const labels = days.map((d) => {
      const dt = new Date(d.date);
      return dt.toLocaleDateString(undefined, { weekday: "short" }).charAt(0);
    });
    const c = new Chart(sessionsBarEl, {
      type: "bar",
      data: {
        labels,
        datasets: [{
          data: days.map((d) => d.count),
          backgroundColor: days.map((d) => d.count > 0 ? hexToRgba("#22c55e", 0.5) : "rgba(255,255,255,0.04)"),
          borderRadius: 3,
          borderSkipped: false,
          barPercentage: 0.6,
          categoryPercentage: 0.7,
        }],
      },
      options: {
        responsive: true, maintainAspectRatio: false,
        plugins: { legend: { display: false }, tooltip: { ...tooltipStyle(), callbacks: { title: (items) => { const idx = items[0]?.dataIndex; return idx != null ? new Date(days[idx].date).toLocaleDateString(undefined, { month: "short", day: "numeric" }) : ""; } } } },
        scales: {
          x: { ticks: { color: "#3f3f46", font: { size: 9 } }, grid: { display: false }, border: { display: false } },
          y: { beginAtZero: true, ticks: { color: "#3f3f46", font: { size: 9 }, stepSize: 1 }, grid: { color: "rgba(255,255,255,0.03)" }, border: { display: false } },
        },
      },
    });
    charts.push(c);
  }

  function makeLineDataset(label, series, color) {
    return {
      label,
      data: (series || []).map((d) => d.count),
      borderColor: color,
      backgroundColor: hexToRgba(color, 0.06),
      fill: true,
      tension: 0.4,
      pointRadius: 0,
      pointHoverRadius: 4,
      borderWidth: 2,
    };
  }

  function tooltipStyle() {
    return {
      backgroundColor: "rgba(24,24,27,0.95)",
      titleColor: "#e4e4e7",
      bodyColor: "#a1a1aa",
      borderColor: "rgba(255,255,255,0.1)",
      borderWidth: 1,
      padding: 10,
      cornerRadius: 6,
    };
  }

  function lineOpts() {
    return {
      responsive: true, maintainAspectRatio: false,
      interaction: { mode: "index", intersect: false },
      plugins: {
        legend: { display: true, position: "top", align: "end", labels: { color: "#a1a1aa", boxWidth: 10, padding: 14, font: { size: 10 }, usePointStyle: true, pointStyle: "circle" } },
        tooltip: tooltipStyle(),
      },
      scales: {
        x: { ticks: { color: "#3f3f46", font: { size: 9 }, maxRotation: 0, maxTicksLimit: 7 }, grid: { color: "rgba(255,255,255,0.03)" }, border: { display: false } },
        y: { beginAtZero: true, ticks: { color: "#3f3f46", font: { size: 9 }, stepSize: 1 }, grid: { color: "rgba(255,255,255,0.03)" }, border: { display: false } },
      },
    };
  }

  function buildDoughnut(el, segments, colors) {
    if (!el) return;
    const total = segments.reduce((a, s) => a + s.value, 0);
    if (total === 0) return;
    const c = new Chart(el, {
      type: "doughnut",
      data: {
        labels: segments.map((s) => s.label),
        datasets: [{
          data: segments.map((s) => s.value),
          backgroundColor: colors,
          borderColor: "rgba(24,24,27,0.8)",
          borderWidth: 2,
          hoverOffset: 4,
        }],
      },
      options: {
        responsive: true, maintainAspectRatio: false, cutout: "72%",
        plugins: {
          legend: { display: false },
          tooltip: tooltipStyle(),
        },
      },
    });
    charts.push(c);
  }

  function buildAllCharts() {
    if (!stats) return;
    destroyAll();

    // Sparklines
    metrics.forEach((m) => {
      const el = sparkEls[m.key];
      if (el) buildSparkline(el, m.sparkData, m.color);
    });

    buildActivityChart();
    buildSessionsBar();

    buildDoughnut(invitePieEl,
      [{ label: t("status.claimed"), value: stats.invites_claimed || 0 }, { label: t("status.pending"), value: stats.invites_pending || 0 }, { label: t("status.expired"), value: stats.invites_expired || 0 }, { label: t("status.revoked"), value: stats.invites_revoked || 0 }],
      ["#22c55e", "#f59e0b", "#52525b", "#ef4444"]
    );
    buildDoughnut(viewerPieEl,
      [{ label: t("status.active"), value: stats.viewers_active || 0 }, { label: t("status.revoked"), value: stats.viewers_revoked || 0 }],
      ["#8b5cf6", "#ef4444"]
    );
    buildDoughnut(sitePieEl,
      [{ label: t("sites.public"), value: stats.sites_public || 0 }, { label: t("sites.private"), value: stats.sites_private || 0 }],
      ["#3b82f6", "#52525b"]
    );
  }

  function formatRelative(d) {
    if (!d) return "-";
    const diff = Date.now() - new Date(d).getTime();
    if (diff < 60000) return t("time.just_now");
    if (diff < 3600000) return `${Math.floor(diff / 60000)}${t("time.m_ago")}`;
    if (diff < 86400000) return `${Math.floor(diff / 3600000)}${t("time.h_ago")}`;
    return new Date(d).toLocaleDateString(undefined, { month: "short", day: "numeric" });
  }

  const statusColors = { claimed: "text-green-500", active: "text-green-500", pending: "text-amber-400", expired: "text-zinc-500", revoked: "text-rose-500" };
  const statusIcons = { claimed: "mdi:check-circle", active: "mdi:check-circle", pending: "mdi:timer-sand", expired: "mdi:clock-outline", revoked: "mdi:close-circle" };

  $effect(() => {
    if (stats && activityChartEl) {
      requestAnimationFrame(buildAllCharts);
    }
  });

  onMount(() => { loadStats(); });
  onDestroy(() => { destroyAll(); });
</script>

<Layout>
  {#if error}
    <div class="px-3 py-2 rounded border border-rose-500/30 bg-rose-500/10 text-sm text-rose-200 mb-4">{error}</div>
  {/if}

  {#if loading}
    <div class="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-5">
      {#each Array(4) as _}
        <div class="p-4 rounded border border-white/10 bg-white/[0.02]"><Skeleton lines={3} /></div>
      {/each}
    </div>
    <div class="ui-card p-5"><Skeleton lines={6} /></div>
  {:else}

    <!-- ── Metric cards with sparklines ── -->
    <div class="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-5">
      {#each metrics as metric, idx (metric.key)}
        <button
          type="button"
          class="relative overflow-hidden p-4 rounded border border-white/10 bg-white/[0.02] hover:bg-white/[0.04] hover:border-white/15 transition-all text-left group"
          onclick={() => push(metric.href)}
        >
          <!-- Sparkline background -->
          <div class="absolute bottom-0 left-0 right-0 h-[40px] opacity-60">
            <canvas bind:this={sparkEls[metric.key]}></canvas>
          </div>

          <div class="relative z-10">
            <div class="flex items-center justify-between mb-2">
              <div
                class="w-8 h-8 rounded flex items-center justify-center"
                style="background: {hexToRgba(metric.color, 0.15)}; color: {metric.color}"
              >
                <Icon icon={metric.icon} class="w-4 h-4" />
              </div>
              <Icon icon="mdi:chevron-right" class="w-4 h-4 text-zinc-700 group-hover:text-zinc-500 transition-colors" />
            </div>
            <p class="text-2xl font-bold text-white tabular-nums">{metric.value}</p>
            <p class="text-[10px] uppercase tracking-wider text-zinc-500 mt-0.5">{metric.label}</p>
            <p class="text-[10px] text-zinc-600 mt-1">{metricSubs[idx]}</p>
          </div>
        </button>
      {/each}
    </div>

    <!-- ── Main content: 2-column on desktop ── -->
    <div class="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-5">

      <!-- Activity line chart (wider) -->
      <div class="lg:col-span-2 ui-card p-4 sm:p-5">
        <h2 class="text-xs font-semibold text-zinc-300 uppercase tracking-wider mb-3">{t("dashboard.activity")} — 30 {t("dashboard.days_suffix")}</h2>
        <div class="h-[180px] sm:h-[240px]">
          <canvas bind:this={activityChartEl}></canvas>
        </div>
      </div>

      <!-- Sessions bar chart (narrower) -->
      <div class="ui-card p-4 sm:p-5">
        <h2 class="text-xs font-semibold text-zinc-300 uppercase tracking-wider mb-3">{t("dashboard.sessions")}</h2>
        <div class="h-[180px] sm:h-[240px]">
          <canvas bind:this={sessionsBarEl}></canvas>
        </div>
      </div>
    </div>

    <!-- ── Breakdown doughnuts + recent activity ── -->
    <div class="grid grid-cols-1 lg:grid-cols-3 gap-4">

      <!-- Doughnut trio -->
      <div class="ui-card p-4 sm:p-5">
        <h2 class="text-xs font-semibold text-zinc-300 uppercase tracking-wider mb-4">{t("dashboard.breakdown")}</h2>
        <div class="space-y-5">
          <!-- Invites -->
          <div class="flex items-center gap-4">
            <div class="w-16 h-16 shrink-0 relative">
              <canvas bind:this={invitePieEl}></canvas>
              {#if !stats.invites_total}
                <div class="absolute inset-0 flex items-center justify-center">
                  <div class="w-12 h-12 rounded-full border-2 border-white/[0.06]"></div>
                </div>
              {/if}
            </div>
            <div class="min-w-0 flex-1">
              <p class="text-sm font-medium text-zinc-200">{t("dashboard.invites")}</p>
              <div class="flex flex-wrap gap-x-3 gap-y-0.5 text-[10px] text-zinc-500 mt-1">
                <span><span class="inline-block w-1.5 h-1.5 rounded-full bg-green-500 mr-1"></span>{stats.invites_claimed || 0} {t("dashboard.claimed")}</span>
                <span><span class="inline-block w-1.5 h-1.5 rounded-full bg-amber-500 mr-1"></span>{stats.invites_pending || 0} {t("dashboard.pending")}</span>
                <span><span class="inline-block w-1.5 h-1.5 rounded-full bg-zinc-600 mr-1"></span>{stats.invites_expired || 0} {t("dashboard.expired")}</span>
                <span><span class="inline-block w-1.5 h-1.5 rounded-full bg-rose-500 mr-1"></span>{stats.invites_revoked || 0} {t("dashboard.revoked")}</span>
              </div>
            </div>
          </div>

          <!-- Viewers -->
          <div class="flex items-center gap-4">
            <div class="w-16 h-16 shrink-0 relative">
              <canvas bind:this={viewerPieEl}></canvas>
              {#if !stats.viewers_count}
                <div class="absolute inset-0 flex items-center justify-center">
                  <div class="w-12 h-12 rounded-full border-2 border-white/[0.06]"></div>
                </div>
              {/if}
            </div>
            <div class="min-w-0 flex-1">
              <p class="text-sm font-medium text-zinc-200">{t("dashboard.viewers")}</p>
              <div class="flex gap-x-3 text-[10px] text-zinc-500 mt-1">
                <span><span class="inline-block w-1.5 h-1.5 rounded-full bg-violet-500 mr-1"></span>{stats.viewers_active || 0} {t("dashboard.active")}</span>
                <span><span class="inline-block w-1.5 h-1.5 rounded-full bg-rose-500 mr-1"></span>{stats.viewers_revoked || 0} {t("dashboard.revoked")}</span>
              </div>
            </div>
          </div>

          <!-- Sites -->
          <div class="flex items-center gap-4">
            <div class="w-16 h-16 shrink-0 relative">
              <canvas bind:this={sitePieEl}></canvas>
              {#if !stats.sites_count}
                <div class="absolute inset-0 flex items-center justify-center">
                  <div class="w-12 h-12 rounded-full border-2 border-white/[0.06]"></div>
                </div>
              {/if}
            </div>
            <div class="min-w-0 flex-1">
              <p class="text-sm font-medium text-zinc-200">{t("dashboard.sites")}</p>
              <div class="flex gap-x-3 text-[10px] text-zinc-500 mt-1">
                <span><span class="inline-block w-1.5 h-1.5 rounded-full bg-blue-500 mr-1"></span>{stats.sites_public || 0} {t("dashboard.public")}</span>
                <span><span class="inline-block w-1.5 h-1.5 rounded-full bg-zinc-600 mr-1"></span>{stats.sites_private || 0} {t("dashboard.private")}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Recent activity (spans 2 cols on desktop) -->
      <div class="lg:col-span-2 ui-card p-4 sm:p-5">
        <h2 class="text-xs font-semibold text-zinc-300 uppercase tracking-wider mb-3">{t("dashboard.recent_activity")}</h2>

        {#if !stats.recent_invites?.length && !stats.recent_sessions?.length}
          <div class="flex flex-col items-center py-10 text-center">
            <Icon icon="mdi:clock-outline" class="w-7 h-7 text-zinc-700 mb-2" />
            <p class="text-sm text-zinc-500">{t("dashboard.no_recent")}</p>
          </div>
        {:else}
          <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <!-- Invites column -->
            {#if stats.recent_invites?.length}
              <div>
                <p class="text-[10px] uppercase tracking-wider text-zinc-600 mb-2">{t("dashboard.invites")}</p>
                <div class="space-y-0.5">
                  {#each stats.recent_invites as item}
                    <div class="flex items-center justify-between gap-2 py-1.5 px-2 rounded hover:bg-white/[0.03] transition-colors">
                      <div class="flex items-center gap-2 min-w-0">
                        <Icon icon={statusIcons[item.status] || "mdi:help-circle"} class="w-3.5 h-3.5 shrink-0 {statusColors[item.status] || 'text-zinc-500'}" />
                        <span class="text-[13px] text-zinc-300 truncate">{item.label || "—"}</span>
                      </div>
                      <span class="text-[10px] text-zinc-600 shrink-0">{formatRelative(item.created_at)}</span>
                    </div>
                  {/each}
                </div>
              </div>
            {/if}
            <!-- Sessions column -->
            {#if stats.recent_sessions?.length}
              <div>
                <p class="text-[10px] uppercase tracking-wider text-zinc-600 mb-2">{t("dashboard.sessions")}</p>
                <div class="space-y-0.5">
                  {#each stats.recent_sessions as item}
                    <div class="flex items-center justify-between gap-2 py-1.5 px-2 rounded hover:bg-white/[0.03] transition-colors">
                      <div class="flex items-center gap-2 min-w-0">
                        <Icon icon={statusIcons[item.status] || "mdi:help-circle"} class="w-3.5 h-3.5 shrink-0 {statusColors[item.status] || 'text-zinc-500'}" />
                        <span class="text-[13px] text-zinc-300 truncate">{item.label || "—"}</span>
                      </div>
                      <span class="text-[10px] text-zinc-600 shrink-0">{formatRelative(item.created_at)}</span>
                    </div>
                  {/each}
                </div>
              </div>
            {/if}
          </div>
        {/if}
      </div>
    </div>

  {/if}
</Layout>
